/**
 * GM commands (development cheats, the GM menu): `{ t: 'gm', cmd }`. Heard only when the World was made with
 * `gm: true` (`World.gmEnabled`); a server world without it ignores them. Moved out of world.ts as is: every
 * sub-command changes the same state, emits the same events and draws from the random stream in the same order.
 */
import { MAX_SKILL_LEVEL } from '../cultivation';
import { NEIGONG } from '../data/neigong';
import { ITEMS } from '../data/items';
import { getMonster } from '../data/monsters';
import { getSchool, SCHOOLS } from '../data/schools';
import { MOUNTS } from '../data/mounts';
import { FORGE_ELEMENT_INFO, FORGE_MAX, INFUSE_MAX, isForgeElement } from '../data/forge';
import { advanceTraining } from '../training';
import { ladderFor } from '../data/ladder';
import { TITLE_LIST, TITLES } from '../data/titles';
import { SKINS } from '../data/skins';
import { QUESTS } from '../data/quests';
import { MARTIAL_ARTS } from '../data/skills';
import { randomFloorIn } from '../map';
import { freeAt } from '../path';
import { allQinggong } from '../qinggong';
import { STATUSES } from '../status';
import { dist, type GmCommand, type GmFlag, type GmTeleport } from '../world';
import type { SystemHost, WorldSystem } from './types';

/** The GM crowd (op 'crowd'): every school's uniform for both sexes, and at most this many at once. */
const CROWD_SPRITES = [
  'heroWudang',
  'heroWudangF',
  'heroShaolin',
  'heroShaolinF',
  'heroJinyiwei',
  'heroJinyiweiF',
  'heroJilegu',
  'heroJileguF',
  'heroJunzitang',
  'heroJunzitangF',
  'heroBlood',
  'heroBloodF',
] as const;
const MAX_CROWD = 300;

export class GmSystem implements WorldSystem {
  readonly name = 'gm';

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    gm: (cmd: { cmd: GmCommand }) => {
      if (this.h.world.gmEnabled) this.run(cmd.cmd);
    },
  };

  private run(c: GmCommand): void {
    const h = this.h;
    const w = h.world;
    const p = w.player;
    const pr = w.progress;
    const done = (text: string) => {
      w.checkTitles();
      w.refreshPlayerStats();
      h.emit({ type: 'progressChanged' });
      h.notice(`GM: ${text}`);
    };
    switch (c.op) {
      case 'cultivation':
        pr.cultivation += c.amount;
        return done(`แต้มฝึกปรือ +${c.amount}`);
      case 'gold':
        pr.gold += c.amount;
        return done(`ตำลึง +${c.amount}`);
      case 'restore':
        Object.assign(p, { hp: p.maxHp, qi: p.maxQi, stamina: p.maxStamina, stunT: 0 });
        p.cooldowns = {};
        return done('ฟื้นฟูเต็ม รีเซ็ตคูลดาวน์');
      case 'rage':
        p.rage = p.maxRage;
        return done('ความโกรธเต็ม');
      case 'toggle': {
        w.gmFlags[c.flag] = !w.gmFlags[c.flag];
        const flagNames: Record<GmFlag, string> = {
          god: 'อมตะ',
          oneHit: 'ตีทีเดียวตาย',
          noCooldown: 'ไม่มีคูลดาวน์',
          infQi: 'ลมปราณ/ความโกรธไม่จำกัด',
        };
        return done(`${flagNames[c.flag]} ${w.gmFlags[c.flag] ? 'เปิด' : 'ปิด'}`);
      }
      case 'learnAllArts':
        pr.arts = Object.values(MARTIAL_ARTS)
          .filter((a) => !a.hidden)
          .map((a) => a.id);
        pr.qinggong = allQinggong();
        return done('เรียนทุกวิชา');
      case 'maxSkills':
        for (const a of Object.values(MARTIAL_ARTS))
          for (const id of a.skills) pr.skillLevels[id] = MAX_SKILL_LEVEL;
        return done('กระบวนท่าทุกท่าขั้นสูงสุด');
      case 'maxNeigong':
        for (const n of Object.values(NEIGONG)) pr.neigong[n.id] = n.maxLevel;
        return done('กำลังภายในทุกเล่มขั้นสูงสุด');
      case 'forge': {
        const id = pr.equipment.weapon;
        if (!id) return h.notice('ไม่ได้ถืออาวุธ');
        const el = isForgeElement(c.el) ? c.el : null;
        const rid = h.forgeable(id);
        const roll = pr.rolls[rid]!;
        pr.rolls[rid] = {
          ...roll,
          forge: {
            plus: Math.min(FORGE_MAX, Math.max(0, Math.floor(c.plus))),
            el,
            lv: el ? Math.min(INFUSE_MAX, Math.max(0, Math.floor(c.lv))) : 0,
          },
        };
        const f = pr.rolls[rid]!.forge!;
        return done(`อาวุธ +${f.plus}${f.el ? ` ธาตุ${FORGE_ELEMENT_INFO[f.el].name} ${f.lv}` : ''}`);
      }
      case 'jade':
        pr.jade += c.amount;
        return done(`+${c.amount} หยกเซียน`);
      case 'finishTraining': {
        const t = pr.training;
        if (!t) return;
        t.left = 0;
        for (const d of advanceTraining(pr, 1).done) w.sys.training.finish(d);
        return done('ฝึกเสร็จทันที');
      }
      case 'hours':
        w.hour = (((w.hour + c.amount) % 24) + 24) % 24;
        return done(`เวลา ${Math.floor(w.hour)}:00 ${w.period.name}`);
      case 'arenaNextDay': {
        const msg = w.arenaNewDay(`${pr.arena.day}+`);
        return done(msg ?? 'ขึ้นวันใหม่');
      }
      case 'ladderRating': {
        const l = pr.ladder[ladderFor(pr.karma)];
        l.rating = Math.max(0, l.rating + c.amount);
        return done(`คะแนนบันได ${c.amount > 0 ? '+' : ''}${c.amount}`);
      }
      case 'passXp':
        h.passXp(c.amount);
        return done(`+${c.amount} แต้มตั๋วยุทธภพ`);
      case 'rivalPk': {
        const r = w.get(w.rivalId);
        if (!r) return;
        r.pk = !r.pk;
        h.emit({ type: 'pk', id: r.id, on: r.pk });
        return done(`ดาบไร้เงา ${r.pk ? 'เปิดแดง' : 'ปิด PK'}`);
      }
      case 'allTitles':
        for (const t of TITLE_LIST) w.grantTitle(t.id);
        for (const id of Object.keys(SKINS)) if (!pr.skins.includes(id)) pr.skins.push(id);
        for (const id of Object.keys(MOUNTS)) if (!pr.mounts.includes(id)) pr.mounts.push(id);
        return done('ได้รับทุกฉายา สกิน และพาหนะ');
      case 'title':
        {
          w.grantTitle(c.id);
          w.wearTitle(c.id);
          // Put on what comes with it.
          const g = TITLES[c.id]?.grants;
          if (g?.skin && SKINS[g.skin]) pr.skinWorn[SKINS[g.skin]!.slot] = g.skin;
          if (g?.mount) pr.mountPick = g.mount;
          p.outfit = h.outfitOf(p);
        }
        return done(`มอบฉายา「${TITLES[c.id]?.name ?? c.id}」`);
      case 'school':
        if (c.id && !SCHOOLS[c.id]) return;
        pr.school = null;
        if (c.id) h.joinSchool(c.id);
        else p.title = pr.title;
        return done(c.id ? `เข้าสำนัก${getSchool(c.id).name}` : 'ออกจากสำนัก');
      case 'giveAllItems':
        for (const id of Object.keys(ITEMS)) if (!pr.inventory.includes(id)) pr.inventory.push(id);
        return done('ได้รับไอเทมทุกชิ้น');
      case 'questSet':
        pr.quest = {
          index: Math.max(0, Math.min(QUESTS.length, c.index)),
          status: 'available',
          progress: [],
        };
        h.emit({ type: 'quest', status: 'progress', questId: w.currentQuest()?.id ?? '' });
        return done(`ภารกิจไปที่ข้อ ${pr.quest.index + 1}`);
      case 'questComplete': {
        const q = w.currentQuest();
        if (!q) return;
        pr.quest.status = 'ready';
        pr.quest.progress = q.objectives.map((o) => o.count);
        h.emit({ type: 'quest', status: 'ready', questId: q.id });
        return done(`ภารกิจ「${q.title}」พร้อมส่ง`);
      }
      case 'spawn': {
        if (!getMonster(c.monster)) return;
        for (let k = 0; k < Math.min(10, c.count); k++) {
          const a = h.random() * Math.PI * 2;
          let [x, y] = [p.x + Math.cos(a) * 2.5, p.y + Math.sin(a) * 2.5];
          if (!freeAt(w.map, x, y, 0.3)) [x, y] = [p.x, p.y];
          if (c.monster === 'boss') h.spawnBoss();
          else h.spawnMonster(c.monster, x, y);
        }
        return done(`เรียก${getMonster(c.monster).name} ×${c.count}`);
      }
      case 'crowd': {
        const n = Math.max(0, Math.min(MAX_CROWD, Math.floor(c.count)));
        for (let k = 0; k < n; k++) {
          const a = h.random() * Math.PI * 2;
          const r = 1.5 + h.random() * 7;
          let [x, y] = [p.x + Math.cos(a) * r, p.y + Math.sin(a) * r];
          if (!freeAt(w.map, x, y, 0.3)) [x, y] = [p.x, p.y];
          const tier = Math.floor(k / CROWD_SPRITES.length) % 5;
          h.add({
            kind: 'ambient',
            x,
            y,
            name: `ศิษย์ ${k + 1}`,
            title: '',
            sprite: CROWD_SPRITES[k % CROWD_SPRITES.length]!,
            outfit: tier ? [`uniform${tier}`] : [],
            speed: 2.2,
            facing: h.random() < 0.5 ? 1 : -1,
            wander: { cx: x, cy: y, r: 3, t: h.random() * 3, tx: null, ty: null },
          });
        }
        return done(`เรียกฝูงชน ×${n}`);
      }
      case 'spawnBoss':
        if (w.boss?.dead) w.boss = null;
        h.spawnBoss();
        return done('เรียกปีศาจกระบี่ทมิฬ');
      case 'killNearby': {
        let n = 0;
        for (const m of w.entities.values())
          if (m.kind === 'monster' && !m.dead && dist(m, p) < 8) {
            h.kill(m, p);
            n++;
          }
        return done(`ปราบศัตรูรอบตัว ${n} ตัว`);
      }
      case 'teleport': {
        const m = w.map;
        const center = (r: readonly [number, number, number, number]) => ({
          x: (r[0] + r[1]) / 2,
          y: (r[2] + r[3]) / 2,
        });
        const to =
          c.to === 'plaza'
            ? m.plaza
            : c.to === 'lair'
              ? { x: m.lair.x, y: m.lair.y - 3 }
              : c.to === 'arena'
                ? { x: m.arena.x, y: m.arena.y + 5 }
                : c.to === 'wolves'
                  ? center(m.regions.wolves)
                  : c.to === 'bandits'
                    ? center(m.regions.bandits)
                    : m.spawn;
        const spot = freeAt(m, to.x, to.y, p.radius)
          ? to
          : randomFloorIn(
              m,
              [Math.floor(to.x) - 2, Math.floor(to.x) + 3, Math.floor(to.y) - 2, Math.floor(to.y) + 3],
              () => h.random(),
            );
        Object.assign(p, { x: spot.x, y: spot.y, path: null, targetId: null });
        h.input.engage = false;
        const names: Record<GmTeleport, string> = {
          spawn: 'จุดเกิด',
          plaza: 'ลานศิลา',
          lair: 'ลานเลือด',
          wolves: 'ป่าหมาป่า',
          bandits: 'ค่ายโจร',
          arena: 'ลานจอมมาร',
        };
        return done(`วาร์ปไป${names[c.to]}`);
      }
      case 'status': {
        const e = c.on === 'self' ? p : w.get(p.targetId);
        if (e && STATUSES[c.id]) w.addStatus(e, c.id, c.stacks);
        return done(
          `ใส่${STATUSES[c.id]?.name ?? c.id} ${c.stacks} ชั้น → ${c.on === 'self' ? 'ตัวเอง' : 'เป้าหมาย'}`,
        );
      }
      case 'clearStatuses':
        p.statuses = [];
        return done('ล้างสถานะตัวเอง');
    }
  }
}
